import { Location, Organization, Practitioner } from "@medplum/fhirtypes";
import { ORGANIZATION_SPECIALTY_KEYWORDS, PRACTITIONER_SPECIALTY_CODES } from "./specialty-codes";

/**
 * Extract medical specialty from practitioner qualifications
 * Priority: First code with specialization > First code with classification > Unknown
 */
export function extractSpecialtyFromPractitioners(practitioners: Practitioner[]): string {
  if (!practitioners || practitioners.length === 0) {
    return "Unknown";
  }

  // Collect all valid taxonomy codes from all practitioners
  const qualifications = practitioners.flatMap(p => p.qualification ?? []);
  const codings = qualifications.flatMap(q => q.code?.coding ?? []);
  const codesFound = codings.flatMap(c =>
    c.code && PRACTITIONER_SPECIALTY_CODES[c.code] ? [c.code] : []
  );

  // First, try to find a code with a specialization
  const codeWithSpecialization = codesFound.find(
    code => !!PRACTITIONER_SPECIALTY_CODES[code]?.specialization
  );
  if (codeWithSpecialization) {
    const entry = PRACTITIONER_SPECIALTY_CODES[codeWithSpecialization];
    if (entry) return entry.specialization;
  }

  // If no specialization found, return the first classification
  if (codesFound.length > 0) {
    const firstCode = codesFound[0];
    if (firstCode) {
      const entry = PRACTITIONER_SPECIALTY_CODES[firstCode];
      if (entry) return entry.classification;
    }
  }

  // Fallback to qualification text search
  const qualificationTexts = qualifications
    .flatMap(q => q.code?.text ?? [])
    .map(text => text.toLowerCase());

  const specialtyFromText = qualificationTexts
    .map(findSpecialtyKeywordMatch)
    .find(specialty => !!specialty);

  return specialtyFromText ?? "Unknown";
}

/**
 * Extract medical specialty from organization names
 */
export function extractSpecialtyFromOrganizations(organizations: Organization[]): string {
  if (!organizations || organizations.length === 0) {
    return "Unknown";
  }

  // Check organization type first
  const typeTexts = organizations.flatMap(org => org.type ?? []).flatMap(type => type.text ?? []);
  const specialtyFromType = typeTexts.map(findSpecialtyKeywordMatch).find(specialty => !!specialty);
  if (specialtyFromType) return specialtyFromType;

  // Fallback to organization name
  const orgNames = organizations.flatMap(org => (org.name ? [org.name.toLowerCase()] : []));
  const specialtyFromName = orgNames.map(findSpecialtyKeywordMatch).find(specialty => !!specialty);
  return specialtyFromName ?? "Unknown";
}

function extractSpecialtyFromLocations(locations: Location[]): string {
  if (!locations || locations.length === 0) {
    return "Unknown";
  }

  const typeTexts = locations
    .flatMap(location => location.type ?? [])
    .flatMap(type => type.text ?? []);

  const specialty = typeTexts.map(findSpecialtyKeywordMatch).find(specialty => !!specialty);
  return specialty ?? "Unknown";
}

function findSpecialtyKeywordMatch(text: string): string | undefined {
  const lowerText = text.toLowerCase();
  const matchedEntry = Object.entries(ORGANIZATION_SPECIALTY_KEYWORDS).find(([keyword]) =>
    lowerText.includes(keyword)
  );
  return matchedEntry?.[1];
}

/**
 * Get specialty for a report with fallback logic
 * Priority: Practitioner specialty > Organization specialty > Location specialty > Unknown
 */
export function getSpecialtyForReport(
  practitioners: Practitioner[],
  organizations?: Organization[],
  locations?: Location[]
): string {
  // First try to extract from practitioners
  const practitionerSpecialty = extractSpecialtyFromPractitioners(practitioners);
  if (practitionerSpecialty !== "Unknown") {
    return practitionerSpecialty;
  }

  // Fallback to organization if available
  if (organizations && organizations.length > 0) {
    const orgSpecialty = extractSpecialtyFromOrganizations(organizations);
    if (orgSpecialty !== "Unknown") {
      return orgSpecialty;
    }
  }

  // Fallback to location if available
  if (locations && locations.length > 0) {
    const locationSpecialty = extractSpecialtyFromLocations(locations);
    if (locationSpecialty !== "Unknown") {
      return locationSpecialty;
    }
  }

  return "Unknown";
}
